import Markdown from "react-markdown";
import { Eraser } from 'lucide-react';
import type { QuestionOption } from '../services/learningTypes';
import { useState } from 'react';

interface MarkdownPreviewProps {
    markdown: string;
    buttonText?: string;
    // Question page specific props
    isQuestionPage?: boolean;
    options?: QuestionOption[];
    correctOptionIds?: string[];
    // Emphasis toggle props
    onToggleEmphasis?: (startIndex: number, endIndex: number) => void;
    onResetEmphasis?: () => void;
    // Admin preview highlighting
    highlightCustomContent?: boolean;
    customizedFallbacks?: Record<string, string>;
}

export default function MarkdownPreview({
    markdown,
    buttonText,
    isQuestionPage = false,
    options = [],
    onToggleEmphasis,
    onResetEmphasis,
    highlightCustomContent = false,
    customizedFallbacks,
}: MarkdownPreviewProps) {

    const [showFallback, setShowFallback] = useState(false);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Check for Ctrl+B (Windows/Linux) or Cmd+B (Mac)
        if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
            e.preventDefault();

            if (!onToggleEmphasis) return;

            const selection = window.getSelection();
            if (!selection || selection.toString().trim() === '') return;

            const selectedText = selection.toString();

            // Find the selected text in the markdown source
            // This is a simple heuristic - finds first occurrence
            const startIndex = markdown.indexOf(selectedText);

            if (startIndex === -1) {
                console.warn('Selected text not found in markdown source');
                return;
            }

            const endIndex = startIndex + selectedText.length;
            onToggleEmphasis(startIndex, endIndex);
        }
    };

    const applyCustomHighlight = (md: string): string =>
        md.replace(/<c>([\s\S]*?)<\/c>/g, (_match, text: string) => `\`${text}\``);

    const highlightPlaceholders = (md: string): string => {
        const startIdx = md.indexOf('{{ai:');
        if (startIdx < 0) return md;

        let depth = 0;
        let i = startIdx;
        while (i < md.length) {
            if (md[i] === '{' && i + 1 < md.length && md[i + 1] === '{') {
                depth++;
                i += 2;
            } else if (md[i] === '}' && i + 1 < md.length && md[i + 1] === '}') {
                depth--;
                if (depth === 0) {
                    const fullMatch = md.substring(startIdx, i + 2);
                    const inner = md.substring(startIdx + 5, i);

                    const firstPipe = inner.indexOf('|');
                    const lastPipe = inner.lastIndexOf('|');

                    const template = (firstPipe >= 0 && lastPipe > firstPipe
                        ? inner.substring(firstPipe + 1, lastPipe)
                        : ''
                    ).trim();
                    const wordRange = lastPipe > firstPipe ? inner.substring(lastPipe + 1).trim() : '';

                    const suffix = wordRange ? ` (${wordRange}w)` : '';
                    const annotatedTemplate = template.replace(
                        /\[\[([\s\S]*?)\]\]/g,
                        (_m, text: string) => `**[AI: ${text}${suffix}]**`
                    );

                    return md.replace(fullMatch, annotatedTemplate);
                }
                i += 2;
            } else {
                i++;
            }
        }

        return md;
    };

    const processedMarkdown = highlightPlaceholders(
        highlightCustomContent ? applyCustomHighlight(markdown) : markdown
    );

    return (
        <div className="h-full flex flex-col relative">
            {/* Reset button in top right corner */}
            {onResetEmphasis && (
                <button
                    type="button"
                    onClick={onResetEmphasis}
                    className="absolute top-2 right-2 z-10 p-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                    title="Remove all underscores from content"
                >
                    <Eraser size={16} className="text-gray-600" />
                </button>
            )}

            <div
                className="flex-1 overflow-y-auto px-4 font-ppmori"
                tabIndex={0}
                onKeyDown={handleKeyDown}
            >
                <div className="flex flex-col h-full items-center justify-center text-center" onClick={() => setShowFallback((prev) => !prev)}>
                    <Markdown
                        key={showFallback ? 'fallback' : 'display'}
                        components={{
                            h1: ({ ...props }) => <h1 className="text-3xl font-bold mt-6 mb-4" {...props} />,
                            h2: ({ ...props }) => <h2 className="text-2xl font-semibold mt-5 mb-3" {...props} />,
                            h3: ({ ...props }) => <h3 className="text-xl font-semibold mt-4 mb-2" {...props} />,
                            h4: ({ ...props }) => <h4 className="text-lg font-semibold mt-3 mb-2" {...props} />,
                            h5: ({ ...props }) => <h5 className="text-base font-semibold mt-2 mb-1" {...props} />,
                            h6: ({ ...props }) => <h6 className="text-sm font-semibold mt-1 mb-1" {...props} />,
                            p: ({ ...props }) => <p className="mb-4 leading-relaxed" {...props} />,
                            ul: ({ ...props }) => <div className="flex items-center justify-center bg-osGrey-5 rounded-4xl px-12 py-8 text-left"><ul className="list-disc list-outside font-semibold !space-y-6" {...props} /></div>,
                            ol: ({ ...props }) => <ol className="list-decimal list-inside mb-4" {...props} />,
                            li: ({ ...props }) => <li className="mb-1" {...props} />,
                            blockquote: ({ ...props }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4" {...props} />,
                            code: ({ className, children, ...props }) => {
                                if (!className && highlightCustomContent) {
                                    const display = String(children);
                                    const fallback = customizedFallbacks?.[display];
                                    return (
                                        <mark
                                            className={`rounded px-0.5 not-italic font-normal cursor-pointer ${showFallback ? 'bg-gray-200 text-gray-500' : 'bg-yellow-200'}`}
                                            title={showFallback ? display : fallback}
                                        >
                                            {showFallback ? fallback : display}
                                        </mark>
                                    );
                                }
                                const variant = className?.replace('language-', '').toLowerCase() as 'correct' | 'incorrect' | 'question' | 'learning' | 'remember' | undefined;
                                if (variant) {
                                    return <div className={`rounded px-1 py-0.5 font-mono text-sm`}>{variant}: {children}</div>;
                                }
                                return (
                                    <code className={className} {...props}>
                                        {children}
                                    </code>
                                );
                            },
                            pre: ({ ...props }) => <pre className="bg-gray-900 text-gray-100 rounded p-4 overflow-x-auto my-4" {...props} />,
                            a: ({ ...props }) => <a className="text-blue-600 underline hover:text-blue-800" target="_blank" rel="noopener noreferrer" {...props} />,
                            img: ({ ...props }) => <img className="max-w-[155px] h-auto rounded my-4" {...props} />,
                            hr: ({ ...props }) => <hr className="my-8 border-gray-300" {...props} />,
                            table: ({ ...props }) => <div className="w-full overflow-x-auto my-4"><table className="min-w-full border-collapse" {...props} /></div>,
                            thead: ({ ...props }) => <thead className="bg-gray-100" {...props} />,
                            tbody: ({ ...props }) => <tbody {...props} />,
                            tr: ({ ...props }) => <tr className="border-b" {...props} />,
                            th: ({ ...props }) => <th className="px-4 py-2 text-left font-semibold" {...props} />,
                            td: ({ ...props }) => <td className="px-4 py-2" {...props} />,
                            strong: ({ ...props }) => <strong className="font-bold" {...props} />,
                            em: ({ ...props }) => <span className="text-osGrey-2" {...props} />,
                            del: ({ ...props }) => <del className="line-through text-gray-500" {...props} />,
                            input: ({ ...props }) => <input className="form-checkbox mr-2" type="checkbox" disabled {...props} />,
                        }}
                    >
                        {processedMarkdown}
                    </Markdown>
                </div>
            </div>

            {/* Hint text for keyboard shortcut */}
            {onToggleEmphasis && (
                <div className="flex-shrink-0 px-4 py-2 text-xs text-gray-500 text-center border-t border-gray-200">
                    Select text and press <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">Ctrl+B</kbd> to toggle emphasis
                </div>
            )}

            {/* Fixed navigation buttons */}
            <div className="flex-shrink-0 p-4 pt-2">
                {isQuestionPage && options.length > 0 ? (
                    <div className="space-y-3">
                        {/* Question answer options */}
                        {options.map((option) => (
                            <div
                                key={option.uid}
                                className="bg-white border-2 border-gray-300 rounded-lg py-3 px-4 text-center font-medium text-gray-700"
                            >
                                <div className="flex items-center justify-center gap-3">
                                    <span>{option.text}</span>
                                </div>
                            </div>
                        ))}

                        {/* Submit button */}
                        <div className="bg-black text-white rounded-lg py-3 px-4 text-center font-medium">
                            {buttonText || 'Submit Answer'}
                        </div>
                    </div>
                ) : (
                    <div className="bg-black text-white rounded-lg py-3 px-4 text-center font-medium">
                        {buttonText || 'Continue'}
                    </div>
                )}
            </div>
        </div>
    );
}